import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@newxsystems/shared";
import { Icon } from "../Icon/Icon";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap transition-colors disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary-500 rounded-lg text-onSurface-100 hover:bg-primary-700 active:bg-primary-900 focus:ring-4 focus:ring-primary-900 disabled:bg-surface-300 disabled:text-onSurface-400",
        outlinePrimary:
          "text-primary-500 ring-1 ring-primary-100 rounded-lg hover:bg-primary-100 active:bg-primary-300 focus:border-0 focus:ring-4 focus:ring-primary-900 disabled:border-surface-300 disabled:text-surface-400 disabled:bg-surface-100",
        outlineGray:
          "text-onSurface-600 ring-1 ring-onSurface-200 rounded-lg hover:bg-surface-200 active:bg-onSurface-300 focus:border-0 focus:ring-4 focus:ring-onSurface-500 disabled:border-surface-300 disabled:text-surface-400 disabled:bg-surface-100",
        text: "text-primary-500 rounded-lg hover:bg-primary-100 active:bg-primary-300 focus:ring-4 focus:ring-primary-900 disabled:text-onSurface-400 disabled:bg-onSurface-100",
        tonal:
          "bg-surface-200 text-onSurface-600 rounded-lg hover:bg-surface-300 active:bg-surface-400 focus:ring-4 focus:ring-onSurface-500 disabled:text-onSurface-400 disabled:bg-surface-200",

        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
        link: "text-primary underline-offset-4 hover:underline",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground",
      },
      size: {
        md: "w-fit h-fit py-3 px-6 font-medium text-[14px]/[24px]",
        sm: "w-fit h-fit py-2 px-4 font-normal text-[12px]/[20px]",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: string | null;
  iconDecorative?: boolean;
  iconPlacement?: "left" | "right" | "leading" | "trailing";
  /** @deprecated Use iconPlacement. Kept for existing consumers with the old typo. */
  iconPlacment?: "left" | "right" | "leading" | "trailing";
}

const isLeadingPlacement = (placement: ButtonProps["iconPlacement"]) =>
  placement === "left" || placement === "leading";

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      icon,
      iconDecorative = true,
      iconPlacement,
      iconPlacment,
      variant,
      size,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const resolvedIconPlacement = iconPlacement ?? iconPlacment ?? "left";
    const iconNode = icon ? (
      <Icon className="size-4" decorative={iconDecorative} name={icon} />
    ) : null;

    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Comp>
      );
    }

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {isLeadingPlacement(resolvedIconPlacement) ? iconNode : null}
        {children}
        {isLeadingPlacement(resolvedIconPlacement) ? null : iconNode}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
Button.displayName = "Ui::Button";
